import { useState, useCallback } from 'react';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { VariableElement, insertVariable } from '../plugins/with-variables';

interface UseVariableHandlingReturn {
  showVariableDialog: boolean;
  setShowVariableDialog: (show: boolean) => void;
  selectedVariable: VariableElement | null;
  handleVariableSelect: (
    variableName: string,
    category: string,
    defaultValue: string | null
  ) => void;
  handleVariableEdit: (element: VariableElement) => void;
}

/**
 * Hook to manage variable functionality in the SMS editor.
 * Handles dialog state, variable insertion, and updates.
 */
export function useVariableHandling(editor: Editor): UseVariableHandlingReturn {
  const [showVariableDialog, setShowVariableDialog] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<VariableElement | null>(null);

  /**
   * Handles the selection of a variable from the dialog
   */
  const handleVariableSelect = useCallback(
    (variableName: string, category: string, defaultValue: string | null) => {
      if (selectedVariable) {
        // If we're editing an existing variable, update it
        try {
          const path = ReactEditor.findPath(editor, selectedVariable);
          Transforms.setNodes<VariableElement>(
            editor,
            { variableName, category, defaultValue },
            { at: path }
          );
        } catch (error) {
          console.warn('Error updating variable:', error);
        }
      } else {
        // Otherwise insert a new variable
        insertVariable(editor, variableName, category, defaultValue);
      }

      // Reset state and close dialog
      setSelectedVariable(null);
      setShowVariableDialog(false);
    },
    [editor, selectedVariable]
  );

  /**
   * Handles clicking on an existing variable to edit it
   */
  const handleVariableEdit = useCallback((element: VariableElement) => {
    setSelectedVariable(element);
    setShowVariableDialog(true);
  }, []);

  return {
    showVariableDialog,
    setShowVariableDialog,
    selectedVariable,
    handleVariableSelect,
    handleVariableEdit
  };
}
